import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Formik } from 'formik'
import { isEmpty, keys } from 'lodash'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer
} from 'components/Filters'
import { Select } from 'components/v2/Select'
import { Tag } from 'components/v2/Tag'
import { GTCompanyV2Vertical } from 'gql/types.generated'
import { IBusinessClientFiltersForm } from 'features/BusinessClients/types'
import { safeFormatMessage } from 'utils/intl'
import { verticalMessages } from 'features/Companies/components/Vertical/messages'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'

const STATUS = {
  active: 'active',
  inactive: 'inactive'
}

const VERTICALS = [
  GTCompanyV2Vertical.Travel,
  GTCompanyV2Vertical.City,
  GTCompanyV2Vertical.Team,
  GTCompanyV2Vertical.Rental,
  GTCompanyV2Vertical.Mobile
]

const statusMessages = defineMessages({
  [STATUS.active]: {
    id: 'dictionary.active',
    defaultMessage: 'Active'
  },
  [STATUS.inactive]: {
    id: 'dictionary.inactive',
    defaultMessage: 'Inactive'
  }
})

const messages = defineMessages({
  selectBusinessClient: {
    id: 'businessClient.filters.selectBusinessClient',
    defaultMessage: 'Select business client'
  },
  selectStatus: {
    id: 'businessClient.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  selectVertical: {
    id: 'businessClient.filters.vertical',
    defaultMessage: 'Select vertical'
  }
})

const defaultValues: IBusinessClientFiltersForm = {
  businessClient: {
    id: '',
    name: ''
  },
  status: '',
  vertical: ''
}

interface IBusinessCustomersFiltersProps {
  filters: IBusinessClientFiltersForm
  onSubmit: (values: IBusinessClientFiltersForm) => void
}

export const BusinessClientFilters = ({ filters, onSubmit }: IBusinessCustomersFiltersProps): ReactElement => {
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const initialValues: IBusinessClientFiltersForm = {
    businessClient: filters.businessClient || defaultValues.businessClient,
    status: filters.status || defaultValues.status,
    vertical: filters.vertical || defaultValues.vertical
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, values } = props

        const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
          const {
            target: { id, value }
          } = event

          handleChange(event)

          onSubmit({
            ...values,
            [id]: value
          })
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
          onSubmit({
            ...values,
            [id]: defaultValues[id]
          })
        }

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <div>
                    <FilterToggle onClick={toggleFilters} />
                  </div>
                  <TagsWrapper>
                    {keys(filters).map((filterKey) => {
                      let label, value
                      let filterValue = filters[filterKey as keyof typeof filters]
                      const hasValue = typeof filterValue === 'boolean' || !isEmpty(filterValue)

                      if (!hasValue) return null
                      switch (filterKey) {
                        case 'businessClient':
                          label = <FormattedMessage id="businessClient.clientId.label" defaultMessage="Client" />
                          filterValue = filterValue as IBusinessClientFiltersForm['businessClient']

                          if (filterValue?.id) {
                            value = filterValue.name
                          }
                          break
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value =
                            statusMessages[filterValue as string] &&
                            intl.formatMessage(statusMessages[filterValue as string])
                          break
                        case 'vertical':
                          label = <FormattedMessage id="businessClient.vertical.label" defaultMessage="Vertical" />
                          value = filterValue
                          break
                      }

                      if (!value) return null

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof filters)}
                        >
                          {label}: {value as string}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <BusinessClientAutocomplete
                    id="businessClient"
                    label={<FormattedMessage id="businessCustomer.id.label" defaultMessage="Business client" />}
                    placeholder={intl.formatMessage(messages.selectBusinessClient)}
                    value={values.businessClient.name}
                    onChange={(businessClient): void => {
                      setFieldValue('businessClient', businessClient)
                      onSubmit({
                        ...values,
                        businessClient
                      })
                    }}
                  />
                  <Select
                    id="status"
                    value={values.status}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectStatus)}</option>
                    {Object.entries(statusMessages).map(([key, value]) => (
                      <option key={key} value={key}>
                        {intl.formatMessage(value)}
                      </option>
                    ))}
                  </Select>
                  <Select
                    id="vertical"
                    value={values.vertical}
                    onChange={handleSelectChange}
                    label={<FormattedMessage id="dictionary.vertical" defaultMessage="Vertical" />}
                  >
                    <option value="">{intl.formatMessage(messages.selectVertical)}</option>
                    {VERTICALS.map((vertical) => (
                      <option key={vertical} value={vertical}>
                        {safeFormatMessage({ intl, messages: verticalMessages, key: vertical })}
                      </option>
                    ))}
                  </Select>
                </FiltersContainer>
              </FiltersWrapper>
            </div>
            <button type="submit" hidden>
              Submit
            </button>
          </form>
        )
      }}
    </Formik>
  )
}
